import { useState } from "react";
import { MapPin, Loader2, Check, X } from "lucide-react";

import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { resolvePinDrop } from "../lib/api";

/** Paste-a-pin-drop box — sits directly under the pickup address field.
 *
 *  Customers send a location three ways: raw coordinates out of their
 *  phone, a shared-location link, or a description. This handles the first
 *  two. The pin is authoritative (exact spot the caller stood), and the
 *  street address it resolves to is what the driver reads — so km bills
 *  from the real position instead of a nearby street number.
 *
 *  Accepts: `-41.2706, 173.2838` · `41.27S 173.28E` · a Google / Apple /
 *  WhatsApp maps link (short links are expanded server-side) · `geo:` URIs
 *  · coordinates buried in a longer text message.
 */
export const PinDropInput = ({ onResolved, testIdPrefix = "pin-drop" }) => {
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const resolve = async (text) => {
    const input = (text ?? raw).trim();
    if (!input) return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const data = await resolvePinDrop(input);
      setResult(data);
      onResolved?.(data);
    } catch (e) {
      setError(
        e?.response?.data?.detail
          || "Couldn't read a location from that. Paste a pin drop, a maps link, or coordinates.",
      );
    } finally {
      setBusy(false);
    }
  };

  const clear = () => {
    setRaw("");
    setResult(null);
    setError("");
  };

  return (
    <div className="mt-3" data-testid={`${testIdPrefix}-wrap`}>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={raw}
            onChange={(e) => {
              setRaw(e.target.value);
              setError("");
            }}
            onPaste={(e) => {
              // Resolve the moment it's pasted — that's the whole point.
              const text = e.clipboardData?.getData("text");
              if (text) {
                e.preventDefault();
                setRaw(text);
                resolve(text);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                resolve();
              }
            }}
            placeholder="GPS / pin drop — paste coordinates or a maps link"
            className="pl-8 pr-8"
            data-testid={`${testIdPrefix}-input`}
          />
          {(raw || result) && (
            <button
              type="button"
              onClick={clear}
              aria-label="Clear pin drop"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              data-testid={`${testIdPrefix}-clear`}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={() => resolve()}
          disabled={busy || !raw.trim()}
          data-testid={`${testIdPrefix}-use-btn`}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Use pin"}
        </Button>
      </div>

      {result && (
        <p
          className="text-[11px] mt-1 text-emerald-700 dark:text-emerald-400 flex items-start gap-1"
          data-testid={`${testIdPrefix}-result`}
        >
          <Check className="h-3.5 w-3.5 shrink-0 mt-px" />
          <span>
            Pinned to {result.lat.toFixed(5)}, {result.lng.toFixed(5)}
            {result.reverse_geocoded
              ? ` — ${result.address}`
              : " — no street address nearby, the pin is exact"}
          </span>
        </p>
      )}
      {error && (
        <p
          className="text-[11px] mt-1 text-red-600 dark:text-red-400"
          data-testid={`${testIdPrefix}-error`}
        >
          {error}
        </p>
      )}
      {!result && !error && (
        <p className="text-[11px] text-slate-400 mt-1">
          Paste what the caller sent — coordinates or a Google / Apple / WhatsApp
          location link. Fills the pickup address above and pins the exact spot.
        </p>
      )}
    </div>
  );
};

export default PinDropInput;
